/**
 * ч.1084 (рішення власника): «Початкові дані (без 1С)» — одна сторінка замість
 * «CSV-імпорт довідників» (/import-csv) і «Вступні залишки» (/opening-balances).
 *
 * Сценарій — перехід НЕ з 1С (інша програма, Excel): спершу довідники з файлу,
 * потім залишки на дату початку обліку. З 1С/BAS усе це робить майстер
 * міграції. Кожен блок — під своїм правом: «Імпорт довідників з файлів»
 * (import.run) і «Налаштування облікової політики» (admin.settings.manage);
 * сторінку видно з будь-яким із них.
 */
import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { openingBalances, type OpeningBalanceListItem } from '@/api/openingBalances';
import { usePermissions } from '@/auth/usePermissions';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import DocBlock from '@/shared/components/DocBlock';
import { useAttachmentFlags } from '@/shared/hooks/useAttachmentFlags';
import RefsFileImport from './RefsFileImport';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const muted = { color: 'var(--text-secondary)', fontSize: '0.85rem' } as const;

function OpeningBalancesList() {
  const navigate = useNavigate();
  const list = useQuery({
    queryKey: ['opening-balances'],
    queryFn: () => openingBalances.list({ limit: 200 }),
  });
  const cols = useMemo<ColumnDef<OpeningBalanceListItem>[]>(() => [
    { key: 'document_number', header: '№', width: 150,
      sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
    { key: 'document_date', header: 'Дата', width: 170,
      sortValue: (r) => r.document_date ?? '',
      cell: (r) => (r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '') },
    { key: 'total_debit', header: 'Σ Дебет', width: 150, align: 'right',
      sortValue: (r) => Number(r.total_debit), cell: (r) => fmt2(r.total_debit) },
    { key: 'total_credit', header: 'Σ Кредит', width: 150, align: 'right',
      sortValue: (r) => Number(r.total_credit), cell: (r) => fmt2(r.total_credit) },
    { key: 'posted', header: 'Проведено', width: 130, align: 'center',
      sortValue: (r) => (r.posted ? 1 : 0), cell: (r) => (r.posted ? '✓ так' : 'ні') },
  ], []);
  // 2026-08-23: колонки «Вкладення»/«Підпис» у списку документів.
  const rows = list.data?.items ?? [];
  const att = useAttachmentFlags<OpeningBalanceListItem>('doc.opening_balances', rows);
  return (
    <DataTable<OpeningBalanceListItem>
      rows={rows}
      columns={[...cols, ...att.column]}
      rowKey={(r) => r.id}
      loading={list.isLoading}
      emptyText="Документів вступних залишків ще немає."
      onRowDoubleClick={(r) => navigate(`/opening-balances/${r.id}`)}
      rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
      initialSortKey="document_date"
      initialSortDir="desc"
    />
  );
}

export default function InitialDataPage() {
  const navigate = useNavigate();
  const perms = usePermissions();
  const canImport = perms.has('import.run');
  const canBalances = perms.has('admin.settings.manage');
  const canMigrate = perms.has('admin.migration.run');

  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      <div>
        <h2 style={{ margin: '0 0 0.35rem' }}>Початкові дані (без 1С)</h2>
        <p style={{ ...muted, margin: 0 }}>
          Для переходу з іншої програми або з Excel: спершу завантажте довідники,
          потім внесіть залишки на дату початку обліку.
          {' '}Переходите з 1С/BAS — скористайтесь{' '}
          {canMigrate ? <Link to="/migration/wizard">майстром міграції</Link> : 'майстром міграції'}:
          він перенесе довідники, залишки та історію сам.
        </p>
      </div>

      <DocBlock index={1} title="Довідники з файлу"
        hint="контрагенти, номенклатура, співробітники, підрозділи, посади">
        {canImport ? <RefsFileImport /> : (
          <p style={{ ...muted, margin: '0 0 0.75rem' }}>
            Потрібне право «Імпорт довідників з файлів» — зверніться до власника акаунта.
          </p>
        )}
      </DocBlock>

      <DocBlock index={2} title="Вступні залишки" hint="залишки на дату початку обліку"
        actions={canBalances ? (
          <button type="button" className="primary"
            onClick={() => navigate('/opening-balances/new')}>
            новий документ
          </button>
        ) : undefined}>
        {canBalances ? (
          <>
            <p style={{ ...muted, margin: '0 0 0.75rem' }}>
              Документ вноситься один раз: залишки за рахунками з вашої попередньої
              програми на дату початку обліку. Проводки створюються через технічний
              рахунок 00. Якщо документ вступних залишків створив майстер міграції
              з 1С, він теж у цьому списку.
            </p>
            <OpeningBalancesList />
          </>
        ) : (
          <p style={{ ...muted, margin: '0 0 0.75rem' }}>
            Потрібне право «Налаштування облікової політики» — зверніться до власника акаунта.
          </p>
        )}
      </DocBlock>
    </div>
  );
}
